import { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Maximize2 } from 'lucide-react';
import SpendingExpandModal from '@/components/dashboard/SpendingExpandModal';

const COLORS = ['#10b981','#3b82f6','#f59e0b','#ef4444','#8b5cf6','#ec4899','#06b6d4','#84cc16','#f97316','#6366f1'];

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  const underBudget = d.budgetTotal !== null && d.overBudget === 0 ? parseFloat((d.budgetTotal - payload[0].value).toFixed(2)) : 0;
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-sm text-sm">
      <p className="font-medium text-slate-700">{d.name}</p>
      <p className="text-slate-600">${payload[0].value.toLocaleString('en-AU', { minimumFractionDigits: 2 })}</p>
      {d.overBudget > 0 && <p className="text-rose-500">Over budget by ${d.overBudget.toLocaleString('en-AU', { minimumFractionDigits: 2 })}</p>}
      {underBudget > 0 && <p className="text-emerald-500">Under budget by ${underBudget.toLocaleString('en-AU', { minimumFractionDigits: 2 })}</p>}
    </div>
  );
};

function getMonthlyBudget(budgets, category) {
  const b = budgets.find(b => b.category === category);
  if (!b) return null;
  return b.period === 'annual' ? b.amount / 12 : b.amount;
}

function getMonthCount(filters) {
  if (!filters) return 1;
  if (filters.periodType === 'month') return 1;
  if (filters.periodType === 'year') return 12;
  if (filters.periodType === 'financial_year') return 12;
  return 1;
}

export default function SpendingByCategoryChart({ transactions, budgets = [], filters }) {
  const [expanded, setExpanded] = useState(false);

  const monthCount = useMemo(() => getMonthCount(filters), [filters]);

  const allData = useMemo(() => {
    const grouped = {};
    transactions
      .filter(t => t.type === 'debit' && t.category)
      .forEach(t => { grouped[t.category] = (grouped[t.category] || 0) + (t.amount || 0); });

    return Object.entries(grouped)
      .map(([name, amount]) => {
        const spend = parseFloat(amount.toFixed(2));
        const mb = getMonthlyBudget(budgets, name);
        const budgetTotal = mb !== null ? parseFloat((mb * monthCount).toFixed(2)) : null;
        const overBudget = budgetTotal !== null ? Math.max(0, parseFloat((spend - budgetTotal).toFixed(2))) : 0;
        return { name, amount: spend, budgetTotal, overBudget };
      })
      .sort((a, b) => b.amount - a.amount);
  }, [transactions, budgets, monthCount]);

  const data = allData.slice(0, 10);

  const CustomYAxisTick = ({ x, y, payload }) => {
    const entry = data.find(d => d.name === payload.value);
    const isOver = entry?.overBudget > 0;
    const maxChars = 18;
    const label = payload.value?.length > maxChars ? payload.value.slice(0, maxChars) + '…' : payload.value;
    return (
      <text x={x} y={y} dy={4} textAnchor="end" fontSize={11} fill={isOver ? '#ef4444' : '#64748b'}>
        <title>{payload.value}</title>
        {label}
      </text>
    );
  };

  const CustomBarLabel = ({ x, y, width, height, value, index }) => {
    const entry = data[index];
    if (value == null) return null;
    const label = `$${value >= 1000 ? (value / 1000).toFixed(1) + 'k' : value.toLocaleString('en-AU', { minimumFractionDigits: 2 })}`;
    return (
      <text x={x + width + 5} y={y + height / 2} dy={4} fontSize={10} fill={entry?.overBudget > 0 ? '#ef4444' : '#64748b'}>
        {label}
      </text>
    );
  };

  return (
    <>
      <Card className="border-0 shadow-sm bg-white">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Spending by Category</CardTitle>
          <button onClick={() => setExpanded(true)} className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors">
            <Maximize2 className="w-3.5 h-3.5" />
          </button>
        </CardHeader>
        <CardContent>
          {data.length === 0 ? (
            <div className="flex items-center justify-center h-56 text-slate-400 text-sm">No expense data for this period</div>
          ) : (
            <ResponsiveContainer width="100%" height={data.length * 30 + 30}>
              <BarChart data={data} layout="vertical" margin={{ left: 0, right: 80, top: 4, bottom: 4 }}>
                <XAxis
                  type="number"
                  tickFormatter={v => `$${v >= 1000 ? (v / 1000).toFixed(0) + 'k' : v}`}
                  fontSize={11} axisLine={false} tickLine={false}
                />
                <YAxis type="category" dataKey="name" fontSize={11} width={130} axisLine={false} tickLine={false} tick={<CustomYAxisTick />} interval={0} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="amount" radius={[0, 4, 4, 0]} maxBarSize={22} label={<CustomBarLabel />}>
                  {data.map((entry, i) => <Cell key={i} fill={entry.overBudget > 0 ? '#ef4444' : COLORS[i % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
      <SpendingExpandModal
        open={expanded}
        onClose={() => setExpanded(false)}
        data={allData}
      />
    </>
  );
}